import { getImagePropertiesFromLogoRatio } from '@constants/file';
import { useAppDispatch, useAppSelector } from '@core/store';
import { useUpdateBranchConfigMutation } from '@features/branch-management/queries';
import { setBranchLogoRatio } from '@features/branch-management/slices/branchConfigSlice';
import { useUpdateOrgConfigMutation } from '@features/settings/queries/OrgSettingsQuery';
import useNotify from '@hooks/notify';
import useLogoRatio, { LogoRatioConfig } from '@hooks/useLogoRatio';
import { setOrgConfig } from '@layouts/main-header/userSlice';
import { LogoRatio, OrgAndBranchId } from '@types';

const LOGO_RATIO_UPDATE_ERROR = 'Failed to update logo ratio';

const useSaveLogoRatio = () => {
  const dispatch = useAppDispatch();
  const { createErrorAlert } = useNotify();
  const { orgLogoRatio } = useLogoRatio();
  const orgConfig = useAppSelector(
    (state) => state.user.orgConfig as LogoRatioConfig | null
  );
  const [updateOrgConfig] = useUpdateOrgConfigMutation();
  const [updateBranchConfig] = useUpdateBranchConfigMutation();

  const saveOrgLogoRatio = async (organizationId: string, ratio: LogoRatio) => {
    if (ratio === orgLogoRatio) return;

    const imageProperties = getImagePropertiesFromLogoRatio(ratio);
    const res = await updateOrgConfig({
      organizationId,
      body: { image_properties: imageProperties }
    });

    if ('error' in res) {
      createErrorAlert(LOGO_RATIO_UPDATE_ERROR);
      return;
    }
    dispatch(setOrgConfig({ ...orgConfig, image_properties: imageProperties }));
  };

  // Always saved: the stored branch ratio may belong to another branch (e.g. new branch creation)
  const saveBranchLogoRatio = async (
    params: OrgAndBranchId,
    ratio: LogoRatio
  ) => {
    const imageProperties = getImagePropertiesFromLogoRatio(ratio);
    const res = await updateBranchConfig({
      params,
      body: { image_properties: imageProperties }
    });

    if ('error' in res) {
      createErrorAlert(LOGO_RATIO_UPDATE_ERROR);
      return;
    }
    dispatch(setBranchLogoRatio(imageProperties));
  };

  return { saveOrgLogoRatio, saveBranchLogoRatio };
};

export default useSaveLogoRatio;
